import React, { useState, useEffect } from 'react';
import { API_BASE, getFullNameByEmail } from '../../App';

const AdminCountResults = ({ token, users }) => {
  const [countPeriods, setCountPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);

  useEffect(() => {
    fetchCountPeriods();
  }, []);

  const fetchCountPeriods = async () => {
    try {
      const response = await fetch(`/detsa/admin/count-period`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCountPeriods(Array.isArray(data) ? data : [data].filter(Boolean));
      }
    } catch (error) {
      console.error('Sayım periyotları yüklenirken hata:', error);
    }
    setLoading(false);
  };

  const fetchSubmissions = async (periodId) => {
    setLoadingSubmissions(true);
    try {
      const response = await fetch(`/detsa/admin/count-submissions/${periodId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSubmissions(data);
      }
    } catch (error) {
      console.error('Sayım gönderimleri yüklenirken hata:', error);
    }
    setLoadingSubmissions(false);
  };

  const handlePeriodSelect = (period) => {
    setSelectedPeriod(period);
    fetchSubmissions(period.id);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSubmissionStats = () => {
    const total = submissions.length;
    const submitted = submissions.filter(s => s.status === 'submitted').length;
    const draft = submissions.filter(s => s.status === 'draft').length;
    
    return { total, submitted, draft };
  };

  const getProductStats = () => {
    let totalProducts = 0;
    let foundProducts = 0;
    let missingProducts = 0;

    submissions.forEach(submission => {
      if (submission.submission_data) {
        Object.values(submission.submission_data).forEach(item => {
          totalProducts++;
          if (item.actual === '1') {
            foundProducts++;
          } else if (item.actual === '0') {
            missingProducts++;
          }
        });
      }
    });

    return { totalProducts, foundProducts, missingProducts };
  };

  if (loading) {
    return <div className="loading">Yükleniyor...</div>;
  }

  return (
    <div className="admin-count-results">
      <h3>Sayım Sonuçları</h3>
      
      {countPeriods.length === 0 ? (
        <div className="no-data">Henüz sayım periyodu oluşturulmamış.</div>
      ) : (
        <>
          <div className="period-selector">
            <h4>Sayım Periyodu Seçin:</h4>
            <div className="period-list">
              {countPeriods.map(period => (
                <button
                  key={period.id}
                  onClick={() => handlePeriodSelect(period)}
                  className={`period-button ${selectedPeriod?.id === period.id ? 'active' : ''}`}
                >
                  <strong>{period.name || period.title}</strong>
                  <br />
                  <small>
                    {formatDate(period.start_date)} - {formatDate(period.end_date)}
                  </small>
                </button>
              ))}
            </div>
          </div>

          {selectedPeriod && (
            <div className="period-results">
              <h4>📊 {selectedPeriod.name || selectedPeriod.title} - Sonuçlar</h4>
              
              {loadingSubmissions ? (
                <div className="loading">Sonuçlar yükleniyor...</div>
              ) : (
                <>
                  <div className="results-stats">
                    <div className="stats-grid">
                      <div className="stat-card">
                        <h3>{getSubmissionStats().total}</h3>
                        <p>Toplam Kullanıcı</p>
                      </div>
                      <div className="stat-card success">
                        <h3>{getSubmissionStats().submitted}</h3>
                        <p>Sayım Tamamlanan</p>
                      </div>
                      <div className="stat-card warning">
                        <h3>{getSubmissionStats().draft}</h3>
                        <p>Taslak</p>
                      </div>
                    </div>
                    
                    {getSubmissionStats().submitted > 0 && (
                      <div className="product-stats">
                        <h5>Ürün İstatistikleri (Tamamlanan Sayımlar)</h5>
                        <div className="stats-grid">
                          <div className="stat-card">
                            <h3>{getProductStats().totalProducts}</h3>
                            <p>Toplam Ürün</p>
                          </div>
                          <div className="stat-card success">
                            <h3>{getProductStats().foundProducts}</h3>
                            <p>Bulunan Ürün</p>
                          </div>
                          <div className="stat-card error">
                            <h3>{getProductStats().missingProducts}</h3>
                            <p>Kayıp Ürün</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="user-submissions">
                    <h5>Kullanıcı Sayımları</h5>
                    {submissions.length === 0 ? (
                      <div className="no-data">Bu periyot için henüz sayım yapılmamış.</div>
                    ) : (
                      <div className="submissions-list">
                        {submissions.map(submission => (
                          <div key={submission.id} className={`submission-card ${submission.status}`}>
                            <div className="submission-header">
                              <div className="submission-user">
                                👤 {getFullNameByEmail(submission.user_email, users)}
                              </div>
                              <span className={`submission-status ${submission.status}`}>
                                {submission.status === 'submitted' ? '✅ Tamamlandı' : '📝 Taslak'}
                              </span>
                            </div>
                            
                            <div className="submission-details">
                              <div className="detail-item">
                                <div className="detail-label">Son Güncelleme</div>
                                <div className="detail-value">{formatDate(submission.updated_at)}</div>
                              </div>
                              {submission.submitted_at && (
                                <div className="detail-item">
                                  <div className="detail-label">Gönderim Tarihi</div>
                                  <div className="detail-value">{formatDate(submission.submitted_at)}</div>
                                </div>
                              )}
                              <div className="detail-item">
                                <div className="detail-label">Toplam Ürün</div>
                                <div className="detail-value">
                                  {Object.keys(submission.submission_data || {}).length}
                                </div>
                              </div>
                              <div className="detail-item">
                                <div className="detail-label">Tamamlanan</div>
                                <div className="detail-value">
                                  {Object.values(submission.submission_data || {}).filter(item => item.actual !== '').length}
                                </div>
                              </div>
                            </div>

                            {submission.status === 'submitted' && submission.submission_data && (
                              <div className="submission-products">
                                <h5>Sayım Detayları</h5>
                                {Object.entries(submission.submission_data).map(([productId, countData]) => {
                                  const isFound = countData.actual === '1';
                                  const isMissing = countData.actual === '0';
                                  const productInfo = countData.product_info;
                                  
                                  return (
                                    <div 
                                      key={productId} 
                                      className={`product-count-result ${isFound ? 'found' : isMissing ? 'missing' : ''}`}
                                    >
                                      <div className="product-name">
                                        {productInfo ? 
                                          `${productInfo.product_name} (${productInfo.product_code})` : 
                                          `Ürün ID: ${productId}`
                                        }
                                      </div>
                                      <div className="count-result">
                                        <span className="result-icon">
                                          {isFound ? '✅' : isMissing ? '❌' : '⏳'}
                                        </span>
                                        <span>
                                          {isFound ? 'Mevcut' : isMissing ? 'Kayıp' : 'Sayılmadı'}
                                        </span>
                                      </div>
                                      {countData.notes && (
                                        <div className="product-notes">
                                          Not: {countData.notes}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminCountResults;